import {z} from 'zod';

import {
  IsoUtcDateTimeSchema,
  MediaFormatSchema,
  MetricNameSchema,
  UuidV4Schema,
} from './common.js';
import {
  ContentEditorialSchema,
  ContentMediaEditorialSchema,
} from './editorial-review.js';
import {
  PieceKindSchema,
  ApprovalJustificationSchema,
  DecisionCommentSchema,
} from './editorial.js';
import {DeploymentEnvironmentSchema} from './platform.js';

/**
 * Contratos de leitura do painel supervisionado.
 *
 * Estes DTOs nascem da prévia que Thiago aprovou
 * (`docs/ui-reference/painel-instagram-devmil/approved-source.html`). Nesta
 * etapa quem os alimenta é uma fixture; os planos 02, 04, 05 e 06 trocam a
 * fonte por adaptadores HTTP sem mexer em componente nem em nome de campo.
 *
 * Duas regras atravessam o arquivo inteiro:
 *
 * 1. indisponibilidade nunca vira zero — daí `MetricValueSchema` e
 *    `MoneyMetricSchema` serem uniões discriminadas por `availability`;
 * 2. bloco alimentado por fixture carrega `illustrative`, para a tela poder
 *    rotular o que é simulado.
 */

// ---------------------------------------------------------------------------
// Peça de conteúdo e os sete portões
// ---------------------------------------------------------------------------

/**
 * O `sha256` do manifesto de publicação, em minúsculas.
 *
 * A MESMA régua de `packages/contracts/src/editorial.ts`, que a rota antiga de
 * aprovação já usava. Ela mora aqui de novo, e não importada de lá, por um
 * motivo de dependência: `editorial.ts` fala do fluxo interno e este arquivo
 * fala da tela; o valor é o mesmo e o formato é conferido nos dois lugares.
 */
const ManifestHashSchema = z.string().regex(/^[0-9a-f]{64}$/u);

/**
 * A espécie da peça, do ponto de vista da TELA.
 *
 * Ela espelha `PieceKindSchema`, e o espelho é conferido por teste: um
 * vocabulário novo do editorial que não chegasse aqui faria o painel receber
 * uma peça que o schema recusa, e a fila do dia sumiria inteira com erro de
 * validação em vez de mostrar cinco de seis.
 *
 * `photo` entrou em 2026-09-05 com o post de foto do dia. Na tela ela muda três
 * coisas: o ícone, o rótulo, e o quadro da revisão, que mostra a imagem em vez
 * do esquema (ver `ContentMediaSchema`, logo abaixo).
 */
export const ContentKindSchema = PieceKindSchema;
export type ContentKind = z.infer<typeof ContentKindSchema>;

export const ContentStatusSchema = z.enum([
  'awaiting_approval',
  'approved',
  'change_requested',
  'rejected',
  'blocked',
  'published',
]);
export type ContentStatus = z.infer<typeof ContentStatusSchema>;

/** Os sete portões aprovados, na ordem em que a prévia os lista. */
export const APPROVED_GATE_ORDER = [
  'source_and_proof',
  'claims_and_numbers',
  'confidentiality',
  'originality',
  'brand_and_readability',
  'media_rights',
  'cta_and_destination',
] as const;

export const QualityGateIdSchema = z.enum(APPROVED_GATE_ORDER);
export type QualityGateId = z.infer<typeof QualityGateIdSchema>;

export const GateOutcomeSchema = z.enum(['pass', 'alert', 'blocked']);
export type GateOutcome = z.infer<typeof GateOutcomeSchema>;

export const GateResultSchema = z.strictObject({
  id: QualityGateIdSchema,
  outcome: GateOutcomeSchema,
});
export type GateResultDto = z.infer<typeof GateResultSchema>;

/**
 * Sete portões, sempre. Nem seis, nem embaralhados: a ordem é o que o revisor
 * humano leu quando aprovou a tela, e ela não pode mudar por acidente.
 */
export const GateResultsSchema = z
  .array(GateResultSchema)
  .length(APPROVED_GATE_ORDER.length)
  .refine(
    (results) =>
      results.every(
        (result, index) => result.id === APPROVED_GATE_ORDER[index],
      ),
    {
      message:
        'gateResults precisa listar os sete portões aprovados, na ordem aprovada',
    },
  );

export const ContentSeriesSchema = z.strictObject({
  id: z.string().min(1),
  /** Nome cheio, usado na revisão individual. */
  name: z.string().min(1),
  /** Nome curto, usado na fila do dia e nas barras de métricas. */
  shortName: z.string().min(1),
});
export type ContentSeriesDto = z.infer<typeof ContentSeriesSchema>;

export const ContentPreviewSchema = z.strictObject({
  brand: z.string().min(1),
  hook: z.string().min(1),
  footer: z.string().min(1),
});
export type ContentPreviewDto = z.infer<typeof ContentPreviewSchema>;

/**
 * Que arquivo de mídia desta versão EXISTE, medido pelo servidor.
 *
 * A tela usa isto para decidir o que desenhar ANTES de pedir byte nenhum. Sem o
 * campo, a única forma de descobrir seria apontar um `<img>` para a rota e
 * esperar: o caminho feliz funcionaria, e a peça ainda em rascunho mostraria o
 * ícone de imagem quebrada no lugar da prévia, na tela onde o dono aprova.
 *
 * `true` significa MEDIDO: existe a linha em `media_objects` e o arquivo está
 * no volume. Tudo que não for essa medição é `false`, incluindo o caso em que
 * a API não consegue olhar o volume. **Dado indisponível nunca vira "tem"**: o
 * pior desfecho de um `false` errado é a prévia esquemática, que é o que a tela
 * já mostrava; o de um `true` errado é o dono aprovando um quadro vazio.
 */
export const ContentMediaSchema = z.strictObject({
  /**
   * Uma IMAGEM que a tela pode desenhar no quadro.
   *
   * Duas coisas caem aqui, e de propósito: a capa JPEG do Reel (a Story sai sem
   * capa do renderizador) e, desde 2026-09-05, a imagem da peça de foto, que
   * NÃO é capa de coisa nenhuma — ela é a publicação inteira.
   *
   * Um campo `photo` separado seria mais literal e pior: a tela faz a MESMA
   * coisa nos dois casos, que é desenhar `<img>` a partir da rota de capa, e um
   * terceiro booleano obrigaria cada leitor a lembrar de tratar os dois juntos.
   * Quem quer distinguir tem `kind`, que já diz.
   */
  cover: z.boolean(),
  /** Vídeo MP4. Reel e Story têm; a peça de foto nunca tem. */
  video: z.boolean(),
  /**
   * Quantos SLIDES do carrossel existem e estão no volume, MEDIDOS.
   *
   * Zero em toda peça que não é carrossel, e zero também num carrossel cujos
   * arquivos sumiram do volume: nos dois casos a tela cai na prévia
   * esquemática, que é o que ela já fazia.
   *
   * É um número, e não um booleano, porque a tela precisa do número: ela
   * desenha "1 / 7" e a navegação entre os slides, e um `true` obrigaria o
   * painel a descobrir quantos são pedindo um por um até levar 404. Ele também
   * é a única forma de a tela dizer a verdade quando faltam slides: cinco de
   * sete é uma peça incompleta, e o dono precisa ver isso ANTES de aprovar.
   */
  slides: z.int().nonnegative(),
});
export type ContentMediaDto = z.infer<typeof ContentMediaSchema>;

export const ContentReviewSchema = z.strictObject({
  id: UuidV4Schema,
  contentVersionId: UuidV4Schema,
  /**
   * O SLOT a que a peça pertence, e o que permite a tela juntar a Reel e o
   * Story do mesmo item num bloco só.
   *
   * O agrupamento poderia ser adivinhado por `scheduledAt` mais `series`, e foi
   * assim que a primeira versão desta tela foi desenhada. O identificador
   * verdadeiro entrou no lugar porque `scheduled_at` é coluna à parte de
   * `local_date` e `scheduled_time` desde a migração 0024, exatamente para o
   * caso em que elas discordam depois de um reagendamento: no dia dessa
   * discordância, o palpite junta peças de itens diferentes ou parte um item em
   * dois, e nada na tela denuncia.
   */
  contentItemId: UuidV4Schema,
  kind: ContentKindSchema,
  series: ContentSeriesSchema,
  title: z.string().min(1),
  caption: z.string().min(1),
  scheduledAt: IsoUtcDateTimeSchema,
  /**
   * O dia LOCAL do item, na mesma régua de `TodayDashboardDto.date`.
   *
   * É a coluna `content_items.local_date`, e não uma conta feita em cima de
   * `scheduledAt`. Ela vem junto porque é ela que o servidor usa para decidir
   * quem continua na fila depois de decidido, e porque é ela que a tela usa
   * para separar "Hoje" de "Amanhã". Recalcular o dia no cliente seria uma
   * segunda conta para a mesma pergunta, e duas contas divergem — foi assim
   * que o painel já anunciou o dia seguinte com a fila do dia corrente.
   */
  localDate: z.iso.date(),
  status: ContentStatusSchema,
  gateResults: GateResultsSchema,
  preview: ContentPreviewSchema,
  /**
   * O que existe de mídia renderizada. Ver `ContentMediaSchema`.
   *
   * Campo OBRIGATÓRIO, e não opcional. Opcional deixaria cada produtor decidir
   * calado se declara ou não, e o ausente viraria "não sei" lido como "não tem"
   * em um lugar e como erro no outro. Obrigatório, quem monta o DTO é forçado a
   * responder a pergunta.
   */
  media: ContentMediaSchema,
  /**
   * O PARECER EDITORIAL desta peça, ou o motivo de não haver um.
   *
   * Obrigatório pelo mesmo motivo de `media`, e com uma razão a mais: aqui o
   * ausente lido errado é pior. Um campo anulável responderia "não tem parecer"
   * e deixaria a tela decidir o que desenhar com isso, que é como um espaço em
   * branco vira "deve estar tudo certo" na cabeça de quem aprova. Com a união
   * discriminada, quem monta o DTO é forçado a dizer POR QUE não há avaliação, e
   * a tela é forçada a desenhar esse estado.
   *
   * Ele NÃO decide nada. `recommendation: 'blocked'` significa "o editor não
   * publicaria isto", e a peça segue para a fila de aprovação como qualquer
   * outra: quem bloqueia peça é `gateResults`, logo acima.
   */
  editorial: ContentEditorialSchema,
  /**
   * O parecer da MÍDIA FINAL desta peça, ou o motivo de não haver um.
   *
   * Campo SEPARADO de `editorial`, e obrigatório pelo mesmo motivo dele. Ele
   * entrou em 2026-09-06 pelo achado F11: o parecer visual era produzido, pago e
   * gravado desde o lote 03, e nunca chegava ao painel. Quem abria a revisão
   * individual lia um bloco chamado "Parecer editorial" e não tinha como saber
   * que ele falava só do texto.
   *
   * Ver `ContentMediaEditorialSchema` para as duas razões de os dois pareceres
   * não caberem num campo só (as réguas e as âncoras são diferentes).
   */
  mediaEditorial: ContentMediaEditorialSchema,
  /**
   * Se esta peça é APROVÁVEL por esta tela, e a identidade do que seria
   * aprovado.
   *
   * ## O hash, e por que ele vem do cartão
   *
   * `manifestHash` é o mesmo `sha256` que a selagem produz (`sealManifest`), e
   * ele resume o que vai ao ar: conteúdo, mídia, horário e identificador do
   * slot. O clique de aprovação devolve ESTE valor, e a API o compara com o
   * estado atual antes de liberar; diferente, a resposta é `409
   * MANIFEST_CHANGED` e a tela precisa ler de novo.
   *
   * Ele existe porque, até 2026-09-06, a rota do painel FABRICAVA o hash no
   * instante do clique (achado F02/F03 da auditoria de 06/09/2026,
   * `routes/panel.ts:218`): a comparação era do estado atual contra ele mesmo,
   * sempre igual, e uma aba aberta antes de um reagendamento aprovava o horário
   * novo sem que ninguém o tivesse lido.
   *
   * ## Por que uma UNIÃO, e não `string | null`
   *
   * O lote 01 entregou o campo como `ManifestHashSchema.nullable()` e deixou
   * anotado o que faltava: aquele `null` cobria DOIS estados diferentes com a
   * mesma mensagem. "Esta peça já foi decidida" e "esta peça não pode ser
   * selada" pedem reações opostas de quem lê — a primeira é o fluxo normal
   * (aprovada ontem, rejeitada agora), a segunda é falta de mídia, de capa ou de
   * política de portões, e alguém precisa ir olhar. A tela dizia "recarregue o
   * painel" nos dois casos, e recarregar não conserta nenhum dos dois.
   *
   * Com a união, quem monta o DTO é obrigado a dizer QUAL dos três é, e a tela é
   * obrigada a desenhar os três. É a mesma doutrina de `ContentEditorialSchema`.
   */
  approval: z.discriminatedUnion('kind', [
    /** Espera decisão, e o manifesto foi selado: o clique pode subir. */
    z.strictObject({
      kind: z.literal('ready'),
      manifestHash: ManifestHashSchema,
    }),
    /**
     * A decisão JÁ foi tomada: aprovada, rejeitada, com alteração pedida ou
     * barrada pelos portões. Não há o que aprovar, e isso não é defeito.
     */
    z.strictObject({kind: z.literal('decided')}),
    /**
     * A peça espera decisão e o manifesto NÃO pôde ser selado.
     *
     * A causa medida é `MANIFEST_INCOMPLETE`: falta mídia, falta capa, ou falta
     * a política de portões. Recarregar não resolve; o que resolve é a esteira
     * terminar a peça, e por isso a tela precisa dizer outra coisa aqui.
     */
    z.strictObject({kind: z.literal('unsealable')}),
  ]),
  /**
   * O que aconteceu com esta peça DEPOIS da liberação.
   *
   * Campo à parte de `status`, e não mais um valor dentro dele. `status` é a
   * DECISÃO do dono (espera, aprovou, pediu alteração, rejeitou, os portões
   * barraram); isto aqui é o que a esteira de publicação fez com a peça já
   * liberada. Enfiar os dois no mesmo enum faria "aprovada" e "publicada"
   * disputarem o mesmo campo, e a tela perderia a diferença entre "o dono
   * liberou e nada saiu" e "o dono liberou e saiu".
   *
   * É o achado F12 pelo lado do resultado: "meta diária completa" embaixo de
   * "Peças geradas" era lido como prova de publicação, e peça gerada não é peça
   * no ar. Sete peças geradas, sete aguardando decisão e zero publicadas é um
   * dia inteiro sem publicar, e a tela dizia "meta diária completa".
   */
  publication: z.discriminatedUnion('state', [
    /** Ainda não liberada. Não há publicação a esperar. */
    z.strictObject({state: z.literal('pending_release')}),
    /** Liberada, e ainda sem item na fila de publicação. */
    z.strictObject({state: z.literal('released')}),
    /** Na fila de publicação, esperando o horário ou já reivindicada. */
    z.strictObject({state: z.literal('scheduled')}),
    /** A publicação saiu. É o único estado que prova post no ar. */
    z.strictObject({state: z.literal('published')}),
    /**
     * O horário passou e a peça não foi publicada nem está sendo tentada.
     *
     * Medido, e não suposto: `scheduledAt` no passado e nenhum item de fila
     * ativo nem concluído para o slot.
     */
    z.strictObject({state: z.literal('expired')}),
    /** A fila de publicação desistiu, ou ficou incerta e precisa de gente. */
    z.strictObject({state: z.literal('failed')}),
  ]),
});
export type ContentReviewDto = z.infer<typeof ContentReviewSchema>;

// ---------------------------------------------------------------------------
// Métricas: indisponível nunca é zero
// ---------------------------------------------------------------------------

export const MetricValueSchema = z.discriminatedUnion('availability', [
  z.strictObject({
    availability: z.literal('available'),
    value: z.number().finite(),
  }),
  z.strictObject({
    availability: z.literal('unavailable'),
    value: z.null(),
  }),
]);
export type MetricValueDto = z.infer<typeof MetricValueSchema>;

export const MoneyMetricSchema = z.discriminatedUnion('availability', [
  z.strictObject({
    availability: z.literal('available'),
    currency: z.literal('BRL'),
    cents: z.int(),
  }),
  z.strictObject({
    availability: z.literal('unavailable'),
    currency: z.literal('BRL'),
    cents: z.null(),
  }),
]);
export type MoneyMetricDto = z.infer<typeof MoneyMetricSchema>;

// ---------------------------------------------------------------------------
// Página Hoje
// ---------------------------------------------------------------------------

export const NextSlotSchema = z.strictObject({
  scheduledAt: IsoUtcDateTimeSchema,
  kind: ContentKindSchema,
  status: z.enum(['valid', 'expired']),
});
export type NextSlotDto = z.infer<typeof NextSlotSchema>;

/**
 * O DIA em números, e a diferença entre gerar e publicar.
 *
 * Os três primeiros já existiam. Os quatro últimos entraram em 2026-09-06 pelo
 * achado F12: o painel contava peça gerada, peça esperando decisão e peça
 * liberada, e parava aí. A tela imprimia "meta diária completa" embaixo do
 * contador de geradas, e "gerada" é a peça que a esteira escreveu — nada nela
 * afirma que alguma coisa foi ao ar. Num dia em que a fila de publicação
 * falhasse inteira, o painel continuaria dizendo "meta diária completa".
 *
 * Os sete são DISJUNTOS por construção: cada peça do dia cai em exatamente um
 * deles, e a soma dos seis últimos é `generated`. Ver `contarPublicacao`, no
 * serviço do snapshot.
 */
export const QueueCountersSchema = z.strictObject({
  /** Peças do dia que existem na fila da tela. Não afirma publicação. */
  generated: z.int().nonnegative(),
  awaiting: z.int().nonnegative(),
  /** Liberada pelo dono e ainda sem item na fila de publicação. */
  released: z.int().nonnegative(),
  /** Na fila de publicação, esperando o horário. */
  scheduled: z.int().nonnegative(),
  /** No ar. É o único número que prova publicação. */
  published: z.int().nonnegative(),
  /** O horário passou sem publicação e sem tentativa em curso. */
  expired: z.int().nonnegative(),
  /** A fila de publicação desistiu ou ficou incerta. */
  failed: z.int().nonnegative(),
  /** Rejeitada, com alteração pedida, ou barrada pelos portões. */
  closed: z.int().nonnegative(),
});
export type QueueCountersDto = z.infer<typeof QueueCountersSchema>;

/**
 * Um alerta do DIA, e não de uma peça.
 *
 * Ele existe porque nem todo problema do dia tem peça onde morar. O primeiro
 * caso é `NO_FRESH_NEWS`: quando não há notícia fresca no banco, a peça de foto
 * simplesmente NÃO é gerada, e o que existe é um item de notícia sem versão
 * nenhuma. Sem este campo, o dono abriria a tela, contaria seis cartões em vez
 * de sete, e não teria como saber se faltou notícia ou se a esteira quebrou.
 *
 * `code` é enum fechado pelo mesmo motivo dos códigos dos portões: mensagem
 * livre não é auditável, e ninguém consegue perguntar a um campo de texto
 * quantos dias do mês ficaram sem notícia.
 *
 * A mensagem vem do servidor porque é ele que sabe o motivo com precisão. A
 * tela não a monta a partir do código: um mapa de código para texto no cliente
 * ficaria para trás no dia em que um código novo nascesse, e o alerta apareceria
 * em branco.
 */
export const TODAY_ALERT_CODES = ['NO_FRESH_NEWS'] as const;
export const TodayAlertSchema = z.strictObject({
  code: z.enum(TODAY_ALERT_CODES),
  message: z.string().min(1).max(400),
});
export type TodayAlertDto = z.infer<typeof TodayAlertSchema>;

export const TodayDashboardSchema = z.strictObject({
  illustrative: z.boolean(),
  date: z.iso.date(),
  /**
   * O que o dia tem a dizer que não cabe em peça nenhuma.
   *
   * Lista, e não campo opcional: dia sem alerta é lista vazia, que é uma
   * medição, e não a ausência do campo, que seria "não sei".
   */
  alerts: z.array(TodayAlertSchema),
  /**
   * A composição do dia, por espécie.
   *
   * `photos` entrou em 2026-09-06 (achado F12, passo 7): o post de foto existe
   * desde 2026-09-05 e o subtítulo da página Hoje continuava anunciando só
   * Reels e Stories. Num dia de 3 Reels, 3 Stories e 1 foto, o subtítulo dizia
   * "3 Reels e 3 Stories" e a fila mostrava sete cartões.
   */
  mix: z.strictObject({
    reels: z.int().nonnegative(),
    stories: z.int().nonnegative(),
    photos: z.int().nonnegative(),
    /**
     * O CARROSSEL entrou em 2026-09-06, com o lote 11.
     *
     * Ele entra no subtítulo pelo mesmo motivo que a foto entrou: num dia de 3
     * Reels, 3 Stories, 1 foto e 1 carrossel, um subtítulo que anunciasse sete
     * peças sobre uma fila de oito cartões seria a mesma mentira de leitura do
     * achado F12, com um número a mais.
     */
    carousels: z.int().nonnegative(),
  }),
  counters: QueueCountersSchema,
  nextSlot: NextSlotSchema.nullable(),
  queue: z.array(ContentReviewSchema).min(1),
});
export type TodayDashboardDto = z.infer<typeof TodayDashboardSchema>;

// ---------------------------------------------------------------------------
// Página MAPA
// ---------------------------------------------------------------------------

/** As quatro etapas do funil, na ordem aprovada. */
export const APPROVED_MAPA_STAGE_ORDER = [
  'started',
  'diagnosed',
  'whatsapp',
  'meeting',
] as const;

export const MapaStageIdSchema = z.enum(APPROVED_MAPA_STAGE_ORDER);
export type MapaStageId = z.infer<typeof MapaStageIdSchema>;

export const MapaStageSchema = z.strictObject({
  id: MapaStageIdSchema,
  count: z.int().nonnegative(),
});
export type MapaStageDto = z.infer<typeof MapaStageSchema>;

export const MapaStagesSchema = z
  .array(MapaStageSchema)
  .length(APPROVED_MAPA_STAGE_ORDER.length)
  .refine(
    (stages) =>
      stages.every(
        (stage, index) => stage.id === APPROVED_MAPA_STAGE_ORDER[index],
      ),
    {
      message:
        'stages precisa listar as quatro etapas aprovadas, na ordem aprovada',
    },
  );

/**
 * Referência pseudonimizada. Nome, telefone, e-mail e @ do Instagram não
 * entram no painel, nem em fixture, nem em snapshot.
 */
export const MapaConversationReferenceSchema = z
  .string()
  .regex(/^lead_[0-9a-f]{4}$/u);

/**
 * Canais de origem, na ordem do mais especifico para o menos.
 *
 * Nomeado, e nao embutido no objeto, porque `@devmil/domain` traduz o que o
 * banco guardou nesta mesma lista: duas copias divergiriam, e a do dominio
 * seria a que ninguem lembraria de atualizar.
 */
export const MAPA_ORIGIN_CHANNELS = [
  'reel',
  'story',
  'instagram',
  'unknown',
] as const;
export const MapaOriginChannelSchema = z.enum(MAPA_ORIGIN_CHANNELS);
export type MapaOriginChannel = z.infer<typeof MapaOriginChannelSchema>;

export const MapaConversationSchema = z.strictObject({
  reference: MapaConversationReferenceSchema,
  stage: MapaStageIdSchema,
  diagnostic: z
    .strictObject({
      answered: z.int().nonnegative(),
      total: z.int().positive(),
    })
    .nullable(),
  /**
   * De onde a conversa veio, quando o evento disser.
   *
   * `unknown` entrou em 2026-09-07, com o passo 5 do lote 09, e ele NAO e o
   * mesmo que `instagram`. Ate aqui toda conversa medida saia com `instagram` e
   * rotulo nulo, escrito a mao no montador do snapshot: uma afirmacao de canal
   * para conversas cuja origem ninguem tinha lido. `instagram` significa "o
   * evento trouxe origem e o canal especifico nao esta na lista de permissao";
   * `unknown` significa "nao existe origem lida".
   */
  origin: z.strictObject({
    channel: MapaOriginChannelSchema,
    label: z.string().min(1).nullable(),
  }),
  window: z.discriminatedUnion('kind', [
    z.strictObject({
      kind: z.literal('countdown'),
      hoursRemaining: z.int().positive(),
    }),
    z.strictObject({kind: z.literal('associated')}),
    z.strictObject({kind: z.literal('completed')}),
  ]),
  nextAction: z.enum([
    'await_whatsapp',
    'redeem_code',
    'await_reply',
    'commercial_event',
  ]),
});
export type MapaConversationDto = z.infer<typeof MapaConversationSchema>;

/**
 * As capacidades do funil, na ordem do percurso da pessoa.
 *
 * A ordem nao e estetica: e o caminho que uma conversa percorre, e a primeira
 * desligada e onde a conversa para.
 *
 * O vocabulario mora AQUI, e nao no dominio, porque `@devmil/domain` depende de
 * `@devmil/contracts` e nao o contrario. O dominio importa esta lista para
 * montar o resumo, e assim existe uma copia so.
 */
export const MAPA_CAPABILITIES = [
  'webhook_ingress',
  'transicao',
  'aviso_de_privacidade',
  'enfileiramento_de_envio',
  'envio_de_mensagem',
  'link_de_whatsapp',
  'resgate_manual',
  'redirecionador_de_campanha',
] as const;
export const MapaCapabilityNameSchema = z.enum(MAPA_CAPABILITIES);
export type MapaCapabilityName = z.infer<typeof MapaCapabilityNameSchema>;

/**
 * Por que uma capacidade esta desligada.
 *
 * `NAO_CONFIGURADA` e decisao de ambiente (falta segredo, falta telefone);
 * `NAO_LIGADA` e porta que existe e ninguem injetou; `NAO_IMPLEMENTADA` e
 * codigo que nao existe; `SEM_APROVACAO_DO_DONO` e texto que precisa de revisao
 * juridica antes de existir. As quatro pedem coisas diferentes de quem le, e por
 * isso nao viraram um booleano so: uma o dono resolve num arquivo de ambiente,
 * outra alguem injeta, outra alguem escreve, e a ultima passa por advogado.
 */
export const MAPA_CAPABILITY_REASONS = [
  'ATIVA',
  'NAO_CONFIGURADA',
  'NAO_LIGADA',
  'NAO_IMPLEMENTADA',
  'SEM_APROVACAO_DO_DONO',
] as const;
export const MapaCapabilityReasonSchema = z.enum(MAPA_CAPABILITY_REASONS);
export type MapaCapabilityReason = z.infer<typeof MapaCapabilityReasonSchema>;

export const MapaCapabilitySchema = z.strictObject({
  capability: MapaCapabilityNameSchema,
  active: z.boolean(),
  reason: MapaCapabilityReasonSchema,
});
export type MapaCapabilityDto = z.infer<typeof MapaCapabilitySchema>;

/**
 * A lista inteira, sempre: as oito capacidades, na ordem do percurso.
 *
 * Comprimento fixo e ordem conferida pelo mesmo motivo de `MapaStagesSchema`:
 * uma lista que pudesse vir curta deixaria a tela esconder a capacidade
 * desligada em vez de mostra-la desligada, que e o oposto do que este bloco
 * existe para fazer.
 */
export const MapaCapabilitiesSchema = z
  .array(MapaCapabilitySchema)
  .length(MAPA_CAPABILITIES.length)
  .refine(
    (lista) =>
      lista.every(
        (item, indice) => item.capability === MAPA_CAPABILITIES[indice],
      ),
    {
      message:
        'capabilities precisa listar as oito capacidades, na ordem do percurso',
    },
  )
  .refine(
    (lista) => lista.every((item) => item.active === (item.reason === 'ATIVA')),
    {
      // Uma capacidade ativa com motivo de ausencia, ou desligada com `ATIVA`,
      // e a tela dizendo duas coisas contrarias na mesma linha.
      message: 'active e reason precisam concordar',
    },
  );

export const MapaDashboardSchema = z.strictObject({
  illustrative: z.boolean(),
  stages: MapaStagesSchema,
  conversations: z.array(MapaConversationSchema),
  /**
   * O que o funil CONSEGUE fazer no processo que esta de pe.
   *
   * Entrou em 2026-09-07, com o passo 2 do lote 09. A auditoria de 06/09/2026
   * mediu "0 conversas reais" e escreveu que injetar a transicao nao prova que
   * `send_template` e `issue_whatsapp_link` foram executados: eles nao sao.
   * Sem este bloco, a tela mostra estagio mudando e nao diz que ninguem do
   * outro lado recebeu resposta.
   *
   * Ele NAO acompanha `illustrative`: capacidade e fato do processo, e nao
   * origem de dado. Mesmo com o funil ilustrativo (zero conversas), a lista de
   * capacidades e medida, porque ela descreve a fiacao e nao a amostra.
   */
  capabilities: MapaCapabilitiesSchema,
});
export type MapaDashboardDto = z.infer<typeof MapaDashboardSchema>;

// ---------------------------------------------------------------------------
// Página Métricas
// ---------------------------------------------------------------------------

/**
 * Quantas pecas formaram o numero, e quantas poderiam ter formado.
 *
 * Media sem denominador nao orienta editorial nenhum: "os Reels alcancaram 800"
 * quer dizer coisas opostas se for uma peca medida de oito ou oito de oito. O
 * par anda junto com a medida, e nao numa legenda solta.
 */
export const MetricSampleSchema = z.strictObject({
  /** Pecas que entraram na conta. */
  measured: z.int().nonnegative(),
  /** Pecas elegiveis: publicadas na janela e com a idade cumprida. */
  eligible: z.int().nonnegative(),
});
export type MetricSampleDto = z.infer<typeof MetricSampleSchema>;

/**
 * Por que a tela nao tem o numero que ela promete.
 *
 * Cada motivo pede uma acao diferente de quem le, e e por isso que eles nao sao
 * um so:
 *
 * - `never_collected`: nunca houve coleta desta medida. Ligar a coleta.
 * - `stale`: a ultima medicao e mais velha que a janela da tela. Esperar a
 *   rotina, ou descobrir por que ela parou.
 * - `window_mismatch`: existe medicao, de OUTRA janela. E o caso do alcance: o
 *   coletor mede um dia fechado, e a tela promete trinta. Somar trinta dias
 *   diarios nao resolve, porque alcance conta pessoas distintas e a mesma
 *   pessoa apareceria trinta vezes. O que existe vai em `instead`.
 * - `meta_unavailable`: a coleta rodou e a Meta nao devolveu a dimensao.
 * - `incomplete_sample`: parte das pecas da janela nao mediu, e o numero seria
 *   um total menor que o real com aparencia de total.
 * - `insufficient_sample`: mediu pouca peca para comparar. Media de uma peca so
 *   nao e desempenho de formato nem de serie.
 */
export const MetricGapSchema = z.enum([
  'never_collected',
  'stale',
  'window_mismatch',
  'meta_unavailable',
  'incomplete_sample',
  'insufficient_sample',
]);
export type MetricGap = z.infer<typeof MetricGapSchema>;

/**
 * Uma medida COM origem: janela medida, instante, versao e amostra.
 *
 * A versao da Graph anda junto porque a mesma metrica muda de definicao entre
 * versoes: comparar dois numeros medidos em versoes diferentes e comparar
 * coisas diferentes, e sem o campo ninguem descobre isso meses depois.
 *
 * O ramo indisponivel carrega `instead`, que e a resposta honesta ao pedido do
 * plano: quando a janela que a Meta serve nao e a que a tela promete, diga qual
 * janela existe em vez de mostrar um traco.
 */
export const SourcedMetricSchema = z.discriminatedUnion('availability', [
  z.strictObject({
    availability: z.literal('available'),
    value: z.number().finite(),
    /** Dias realmente medidos. Pode ser menor que `windowDays` da tela. */
    measuredWindowDays: z.number().positive(),
    measuredAt: IsoUtcDateTimeSchema,
    sourceRevision: z.string().min(1),
    /** Nulo quando a medida e da conta, e nao uma conta sobre pecas. */
    sample: MetricSampleSchema.nullable(),
  }),
  z.strictObject({
    availability: z.literal('unavailable'),
    value: z.null(),
    gap: MetricGapSchema,
    /** A medicao que EXISTE no lugar da pedida. Nulo quando nao existe nenhuma. */
    instead: z
      .strictObject({
        value: z.number().finite(),
        measuredWindowDays: z.number().positive(),
        measuredAt: IsoUtcDateTimeSchema,
        sourceRevision: z.string().min(1),
      })
      .nullable(),
    sample: MetricSampleSchema.nullable(),
  }),
]);
export type SourcedMetricDto = z.infer<typeof SourcedMetricSchema>;

/**
 * Desempenho de um formato numa idade.
 *
 * A idade e obrigatoria porque o acumulado de uma peca sobe com o tempo:
 * comparar um Reel de um dia com uma foto de sete e comparar idades, e nao
 * formatos. A rotina mede em 1, 3, 7 e 30 dias justamente para a comparacao ter
 * onde acontecer.
 */
export const FormatPerformanceSchema = z.strictObject({
  format: MediaFormatSchema,
  ageDays: z.int().positive(),
  metric: MetricNameSchema,
  /** Media por peca da amostra. Indisponivel nunca vira zero. */
  average: SourcedMetricSchema,
});
export type FormatPerformanceDto = z.infer<typeof FormatPerformanceSchema>;

/**
 * Cobertura da coleta na janela: quanto do que foi publicado foi medido.
 *
 * E o numero que diz se a comparacao acima merece confianca. Uma media boa
 * sobre duas de oito pecas e uma media de duas pecas.
 */
export const MetricsCoverageSchema = z.strictObject({
  /** Ultima medicao DISPONIVEL da conta. Nulo quando nunca houve. */
  lastMeasuredAt: IsoUtcDateTimeSchema.nullable(),
  publishedPieces: z.int().nonnegative(),
  measuredPieces: z.int().nonnegative(),
  /** Pecas publicadas sem formato legivel: elas nao tem como ser medidas. */
  piecesWithoutFormat: z.int().nonnegative(),
});
export type MetricsCoverageDto = z.infer<typeof MetricsCoverageSchema>;

/**
 * Desempenho de uma serie.
 *
 * Era um indice de 0 a 100 marcado como ILUSTRATIVO na tela, e ele saiu: um
 * numero inventado ao lado de numeros medidos e o tipo de coisa que orienta
 * decisao editorial sem ninguem perceber que orientou. O que entra no lugar e
 * alcance medio por peca, com amostra e denominador, e indisponivel quando a
 * amostra nao da para comparar.
 */
export const SeriesScoreSchema = z.strictObject({
  series: ContentSeriesSchema,
  /** Alcance medio por peca da serie na janela. */
  reach: SourcedMetricSchema,
});
export type SeriesScoreDto = z.infer<typeof SeriesScoreSchema>;

/**
 * Uma peca que se destacou, com a evidencia inteira.
 *
 * Antes o candidato trazia `retentionPercent` e `mapaStarted`, que nao tem
 * coluna nenhuma no schema: a lista vinha da fixture ilustrativa ou vazia. Um
 * candidato so entra na tela descrito por medicao, com periodo e amostra, para
 * a recomendacao separar observacao de causa: a peca alcancou mais que a media
 * do seu formato e idade, e isso e uma observacao, nao uma explicacao.
 */
export const CandidateSchema = z.strictObject({
  id: UuidV4Schema,
  title: z.string().min(1),
  format: MediaFormatSchema,
  metric: MetricNameSchema,
  /** O que foi medido nesta peca. */
  value: z.number().finite(),
  /** Idade da peca na medicao, em dias. */
  ageDays: z.int().positive(),
  measuredAt: IsoUtcDateTimeSchema,
  sourceRevision: z.string().min(1),
  /** A media do grupo com que ela foi comparada, e o tamanho dele. */
  cohortAverage: z.number().finite(),
  cohortSize: z.int().positive(),
});
export type CandidateDto = z.infer<typeof CandidateSchema>;

export const MetricsDashboardSchema = z.strictObject({
  illustrative: z.boolean(),
  windowDays: z.int().positive(),
  reach: SourcedMetricSchema,
  saves: SourcedMetricSchema,
  savesRateOfReach: MetricValueSchema,
  mapaStarted: MetricValueSchema,
  attributedRevenue: MoneyMetricSchema,
  coverage: MetricsCoverageSchema,
  formatPerformance: z.array(FormatPerformanceSchema),
  series: z.array(SeriesScoreSchema).min(1),
  candidates: z.array(CandidateSchema),
});
export type MetricsDashboardDto = z.infer<typeof MetricsDashboardSchema>;

// ---------------------------------------------------------------------------
// Página Operação
// ---------------------------------------------------------------------------

export const AutonomyLevelValueSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
]);
export type AutonomyLevelValue = z.infer<typeof AutonomyLevelValueSchema>;

export const AutonomyLevelSchema = z.strictObject({
  level: AutonomyLevelValueSchema,
  description: z.string().min(1),
  eligibility: z.discriminatedUnion('kind', [
    z.strictObject({kind: z.literal('active')}),
    z.strictObject({
      kind: z.literal('after_days'),
      days: z.int().positive(),
    }),
  ]),
});
export type AutonomyLevelDto = z.infer<typeof AutonomyLevelSchema>;

export const HealthStatusSchema = z.enum(['healthy', 'degraded', 'failed']);
export type HealthStatus = z.infer<typeof HealthStatusSchema>;

export const OperationHealthSchema = z.strictObject({
  metaToken: HealthStatusSchema,
  publishQueue: z.strictObject({uncertain: z.int().nonnegative()}),
  webhooks: HealthStatusSchema,
  lastVerifiedBackupAt: IsoUtcDateTimeSchema.nullable(),
  nextRestoreDrillInDays: z.int().nonnegative().nullable(),
});
export type OperationHealthDto = z.infer<typeof OperationHealthSchema>;

export const OperationDashboardSchema = z.strictObject({
  illustrative: z.boolean(),
  levels: z.array(AutonomyLevelSchema).length(3),
  health: OperationHealthSchema,
});
export type OperationDashboardDto = z.infer<typeof OperationDashboardSchema>;

// ---------------------------------------------------------------------------
// Estado global da operação
// ---------------------------------------------------------------------------

export const PanelConnectionSchema = z.enum([
  'connected',
  'disconnected',
  'reauth_required',
]);
export type PanelConnection = z.infer<typeof PanelConnectionSchema>;

export const OperationStateSchema = z.strictObject({
  environment: DeploymentEnvironmentSchema,
  connection: PanelConnectionSchema,
  paused: z.boolean(),
  configuredAutonomyLevel: AutonomyLevelValueSchema,
  effectiveAutonomyLevel: AutonomyLevelValueSchema,
});
export type OperationStateDto = z.infer<typeof OperationStateSchema>;

export const PanelSnapshotSchema = z.strictObject({
  operation: OperationStateSchema,
  today: TodayDashboardSchema,
  mapa: MapaDashboardSchema,
  metrics: MetricsDashboardSchema,
  operationDashboard: OperationDashboardSchema,
});
export type PanelSnapshotDto = z.infer<typeof PanelSnapshotSchema>;

// ---------------------------------------------------------------------------
// Ações supervisionadas
// ---------------------------------------------------------------------------

/**
 * Chave de idempotência gerada no clique, nunca no render: dois cliques na
 * mesma peça precisam colidir, e dois renders não.
 */
const IdempotencyKeySchema = UuidV4Schema;

/**
 * A justificativa é OPCIONAL no formato e OBRIGATÓRIA na regra.
 *
 * Quem decide se ela é exigida são os portões da versão, e portão só se lê no
 * servidor: peça limpa aprova sem ressalva, peça com alerta não aprova sem ela.
 * Por isso o schema deixa o campo faltar e o serviço recusa com `428`
 * `JUSTIFICATION_REQUIRED` quando falta onde importa. O que o schema garante é
 * que, existindo texto, ele é o mesmo texto que o domínio aceitaria: sem esta
 * linha o painel não tinha COMO mandar a ressalva, e o dono ficava preso num
 * toast que pedia uma justificativa sem campo para escrevê-la.
 */
export const ApproveContentInputSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  idempotencyKey: IdempotencyKeySchema,
  justification: ApprovalJustificationSchema.optional(),
  /**
   * O hash do manifesto que a TELA mostrou, devolvido no clique.
   *
   * OBRIGATÓRIO, e é a diferença entre aprovar o que foi lido e aprovar o que
   * está no banco neste instante. Até 2026-09-06 o campo não existia e a rota
   * do painel selava a versão no momento do clique para preencher
   * `expectedManifestHash` sozinha (achado F03 da auditoria de 06/09/2026):
   * a comparação do domínio passava a ser do estado atual contra ele mesmo,
   * nunca reprovava, e uma aba carregada antes de um reagendamento liberava um
   * horário que ninguém tinha visto.
   *
   * Ele vem de `ContentReviewDto.approval`, do mesmo retrato que desenhou o
   * cartão, e só o ramo `ready` o tem. Opcional seria pior que ausente: quem esquecesse de mandar voltaria
   * ao comportamento antigo sem nenhum sinal.
   */
  expectedManifestHash: ManifestHashSchema,
});
export type ApproveContentInput = z.infer<typeof ApproveContentInputSchema>;

export const ApprovalResultSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  status: ContentStatusSchema,
  manifestSealedAt: IsoUtcDateTimeSchema,
  counters: QueueCountersSchema,
});
export type ApprovalResultDto = z.infer<typeof ApprovalResultSchema>;

export const RequestContentChangeInputSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  comment: z.string().trim().min(3).max(500),
  idempotencyKey: IdempotencyKeySchema,
});
export type RequestContentChangeInput = z.infer<
  typeof RequestContentChangeInputSchema
>;

export const ChangeRequestResultSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  status: z.literal('change_requested'),
  requestedAt: IsoUtcDateTimeSchema,
  counters: QueueCountersSchema,
});
export type ChangeRequestResultDto = z.infer<typeof ChangeRequestResultSchema>;

/**
 * Rejeitar é terminal: a peça não vai ao ar e não volta para a fila. Por isso o
 * motivo é mais longo que o de alteração (até 1000, como o plano `02` define
 * para o endpoint de rejeição) e por isso a ação é destacada em vermelho.
 */
export const RejectContentInputSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  comment: z.string().trim().min(3).max(1000),
  idempotencyKey: IdempotencyKeySchema,
});
export type RejectContentInput = z.infer<typeof RejectContentInputSchema>;

export const RejectResultSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  status: z.literal('rejected'),
  rejectedAt: IsoUtcDateTimeSchema,
  counters: QueueCountersSchema,
});
export type RejectResultDto = z.infer<typeof RejectResultSchema>;

export const EmergencyPauseInputSchema = z.strictObject({
  paused: z.boolean(),
  idempotencyKey: IdempotencyKeySchema,
});
export type EmergencyPauseInput = z.infer<typeof EmergencyPauseInputSchema>;

/**
 * Regenerar: a única ação do painel que manda a esteira PRODUZIR de novo.
 *
 * As outras três são decisão e param no banco. Esta aposenta a versão atual,
 * abre a seguinte vazia e dispara a reexecução do dia, que é o passo que
 * escreve o roteiro novo. Até 2026-09-04 os dois passos eram `curl` de runbook,
 * e o painel só oferecia "Pedir alteração", que registra uma decisão e não gera
 * nada: quem seguisse a tela via a peça sair da fila e não voltar.
 *
 * **Sem chave de idempotência, de propósito.** As três decisões carregam uma
 * porque dois cliques precisam colidir na mesma escrita; aqui a colisão que
 * importa é a de duas reexecuções do mesmo dia, e quem a impede é a reserva de
 * `generation_runs.claimed_at`, no banco. Uma chave no corpo daria a impressão
 * de uma cerca que não é ela que segura. A rota antiga
 * (`POST /v1/content-versions/:id/regenerate`) também não tem.
 */
export const RegenerateContentInputSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  /**
   * O motivo, na MESMA régua da justificativa da aprovação: 3 a 1000.
   *
   * Ele vai para `content_history` com a ação `regenerated`, e é a única coisa
   * que responde, meses depois, por que aquela versão foi jogada fora.
   */
  reason: DecisionCommentSchema,
  /**
   * Trocar o assunto junto, e não só o texto.
   *
   * `false` regenera sobre a MESMA pauta: o dono não gostou de como ficou.
   * `true` diz que o problema é o assunto, e a esteira escolhe outra pauta
   * elegível da mesma combinação da grade, excluindo a atual. Booleano
   * obrigatório, e não opcional: quem chama é forçado a responder a pergunta,
   * e o ausente não vira "não" calado.
   */
  changeTopic: z.boolean(),
});
export type RegenerateContentInput = z.infer<
  typeof RegenerateContentInputSchema
>;

/**
 * O que a rota devolve, em milissegundos.
 *
 * `status` é `regenerando`, e não `regenerado`: quando esta resposta chega, a
 * versão nova existe e está VAZIA. Quem a preenche é a reexecução do dia, que
 * foi disparada sem espera e leva minutos. Dizer "pronto" aqui seria mentir
 * sobre o único passo que produz texto.
 *
 * `runId` é anulável porque a execução do dia pode não existir: peça criada
 * fora de uma rodada não tem o que reexecutar, e nesse caso o `null` é a
 * resposta honesta, e a versão vazia continua esperando alguém abrir o dia.
 */
export const RegenerateResultSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  /** O número da versão que acabou de nascer, sempre a atual mais um. */
  novaVersao: z.int().positive(),
  runId: UuidV4Schema.nullable(),
  status: z.literal('regenerando'),
  /**
   * O instante do pedido, pelo relógio do SERVIDOR.
   *
   * Ele entrou em 2026-09-06 (achado F12) para a aba conseguir acompanhar a
   * reexecução sem recarregar. `PanelRunStatusDto.finishedAt` sozinho não
   * serve: a regeneração não zera `generation_runs.finished_at`, então o
   * carimbo que está lá quando esta resposta chega é o da execução anterior, e
   * a tela anunciaria o fim antes de a nova começar.
   *
   * Do servidor, e não do relógio do navegador: os dois carimbos precisam vir
   * da MESMA fonte para a comparação significar alguma coisa. É a mesma lição
   * do `mtime` contra `now()` em `spool-media-renderer.ts`.
   */
  requestedAt: IsoUtcDateTimeSchema,
});
export type RegenerateResultDto = z.infer<typeof RegenerateResultSchema>;

/**
 * O ESTADO da execução do dia, para a aba que acabou de mandar regenerar.
 *
 * ## O achado
 *
 * `POST /v1/panel/regenerate` devolve `status: 'regenerando'` e a versão nova
 * VAZIA, e quem preenche é a reexecução do dia, disparada sem espera. Até
 * 2026-09-06 o painel mostrava um toast dizendo "volta à fila em alguns
 * minutos" e nunca mais falava do assunto (achado F12): a peça saía da lista, a
 * aba ficava parada, e a única forma de saber o desfecho era recarregar às
 * cegas. Se a geração falhasse, nada aparecia — a aba prometia um retorno que
 * podia nunca acontecer.
 *
 * ## Por que uma leitura, e não um empurrão do servidor
 *
 * Porque um canal de eventos abriria uma conexão viva por aba aberta, para uma
 * pergunta que acontece algumas vezes por dia e cuja resposta demora minutos. O
 * painel consulta este endereço com intervalo e TETO (ver `PanelProvider`), e o
 * teto é o que impede uma aba esquecida de bater na API para sempre.
 *
 * ## O que ele NÃO devolve
 *
 * Nem `items` nem `alerts`. Os dois carregam identificador de peça e o motivo
 * das falhas, que é diagnóstico interno; a tela precisa de uma coisa só, que é
 * "acabou, e como acabou". A rota interna continua servindo o diagnóstico
 * completo a quem tem mTLS.
 */
export const PANEL_RUN_STATES = [
  /** A execução existe e ainda não começou. */
  'queued',
  /** Está rodando agora. */
  'running',
  /** Terminou com parte das peças resolvida. A regenerada pode estar pronta. */
  'partial',
  /** Terminou com todas as peças do dia resolvidas. */
  'ready',
  /** Terminou sem resolver nenhuma peça. */
  'failed',
] as const;
export const PanelRunStateSchema = z.enum(PANEL_RUN_STATES);
export type PanelRunState = z.infer<typeof PanelRunStateSchema>;

export const PanelRunStatusSchema = z.strictObject({
  runId: UuidV4Schema,
  localDate: z.iso.date(),
  status: PanelRunStateSchema,
  startedAt: IsoUtcDateTimeSchema.nullable(),
  /**
   * Quando a execução terminou, pelo relógio do SERVIDOR.
   *
   * Comparado com `RegenerateResultDto.requestedAt`, ele é o que responde
   * "acabou?", e `status` sozinho não responde. Duas armadilhas medidas no
   * código de hoje:
   *
   * 1. A regeneração devolve o dia de `ready` para `partial` ANTES de disparar
   *    a reexecução (`release-repository.ts`, `regenerateVersion`), e `partial`
   *    também é um desfecho terminal. Uma tela que olhasse só o estado
   *    anunciaria o fim no primeiro instante da espera.
   * 2. Aquele mesmo `update` não zera `finished_at`, então o carimbo que está
   *    lá é o da execução ANTERIOR. Só a comparação com o instante do pedido
   *    separa "terminou agora" de "terminou ontem".
   *
   * `null` é a execução que nunca terminou nenhuma vez.
   */
  finishedAt: IsoUtcDateTimeSchema.nullable(),
});
export type PanelRunStatusDto = z.infer<typeof PanelRunStatusSchema>;
